import React from 'react';

import Panel from '../../design-system/Panel';
import Typography from '../../design-system/Typography';
import Button from '../../design-system/Button';
import { spacing, borderRadius } from '../../design-system/tokens';
import { htmlToParagraphs } from './htmlToParagraphs';

/**
 * Pedido finalizado — tela de confirmação/acompanhamento do pedido.
 *
 * Specs lidos do Figma (node 9470:14381). Por local: cabeçalho (título azul +
 * "Local n de m") + divisor tracejado + bloco de confirmação (badge de status,
 * stepper de etapas, protocolo, descrição). Composite de feature.
 */

export interface OrderConfirmationLocation {
  id: string;
  /** Nome do local (ex.: "Refeitório dos fundos"). */
  title: string;
  /** Data/hora do pedido (ex.: "20/02/2026 às 18:43"). */
  createdAt: string;
  /** Mensagem curta de status (ex.: "Seu pedido está em análise"). */
  statusMessage: string;
  /** Nº do protocolo. */
  protocol: string;
  /** Índice da etapa atual no stepper (0-based). @default 0 */
  currentStep?: number;
  /** Texto explicativo abaixo do stepper. */
  description: React.ReactNode;
  /** Pedido não foi criado (erro na solicitação): badge/frase em vermelho e stepper truncado. */
  isError?: boolean;
}

export interface OrderConfirmationProps {
  locations: OrderConfirmationLocation[];
  /** Rótulo do badge de status. @default 'Em processamento' */
  statusLabel?: string;
  /** Ação do botão "Voltar para a home". */
  onBackHome?: () => void;
}

const STEPS = [
  'Processando',
  'Pedido criado',
  'Em análise',
  'Rota de entrega',
  'Pedido entregue',
];

// ── Ícones dos passos (12×12) ───────────────────────────────────────────────

const GearIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    width="14"
    height="14"
  >
    <path
      d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const ReceiptIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    width="12"
    height="12"
  >
    <path
      d="M4 2h8v12l-2-1-2 1-2-1-2 1z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path
      d="M6 6h4M6 9h4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const ClockIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    width="12"
    height="12"
  >
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M8 5v3l2 1.5"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TruckIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    width="12"
    height="12"
  >
    <path
      d="M1.5 4h8v6h-8zM9.5 6h3l2 2v2h-5z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <circle cx="4" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="11.5" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const CheckIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    width="12"
    height="12"
  >
    <path
      d="M3 8.5l3 3 7-7"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AlertIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    width="14"
    height="14"
  >
    <path
      d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const STEP_ICONS = [
  <GearIcon />,
  <ReceiptIcon />,
  <ClockIcon />,
  <TruckIcon />,
  <CheckIcon />,
];

// ── Estilos (cores exatas do Figma) ─────────────────────────────────────────

const pillBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '8px 12px',
  borderRadius: borderRadius.full,
  fontFamily: "'Mangueira', sans-serif",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

/** Pill do stepper: ativo (azul) vs inativo (cinza). */
const stepPillStyle = (active: boolean): React.CSSProperties => ({
  ...pillBase,
  border: `1px solid ${active ? '#000FFF' : '#8C8C8C'}`,
  background: active ? '#E5E7FF' : '#F8F8F8',
  color: active ? '#003333' : '#8C8C8C',
});

/** Badge principal "Em processamento" (ciano). */
const mainBadgeStyle: React.CSSProperties = {
  ...pillBase,
  flexShrink: 0,
  border: '1px solid #00FFFF',
  background: '#E5FFFF',
  color: '#003333',
};

/** Badge de erro "Erro na solicitação" (vermelho). */
const errorBadgeStyle: React.CSSProperties = {
  ...pillBase,
  flexShrink: 0,
  border: '1px solid #FF4D4F',
  background: '#FFECEC',
  color: '#CF1322',
};

const ERROR_TEXT = '#CF1322';

const StatusStepper = ({
  current,
  isError = false,
}: {
  current: number;
  isError?: boolean;
}): JSX.Element => {
  // No erro, o pedido não progrediu: mostra apenas "Processando".
  const steps = isError ? STEPS.slice(0, 1) : STEPS;
  return (
    <div
      style={{
        display: 'flex',
        gap: spacing.sm,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {steps.map((label, i) => (
        <span key={label} style={stepPillStyle(i === current)}>
          {STEP_ICONS[i]}
          {label}
        </span>
      ))}
    </div>
  );
};

const ConfirmationBlock = ({
  loc,
  statusLabel,
}: {
  loc: OrderConfirmationLocation;
  statusLabel: string;
}): JSX.Element => {
  const isError = !!loc.isError;
  const messageColor = isError ? ERROR_TEXT : '#0B29FB';
  const badgeLabel = isError ? 'Erro na solicitação' : statusLabel;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      {/* Header: título + data | badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: spacing.md,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}
        >
          <Typography as="p" size="2xl" weight="bold" color="#3D3D3D">
            Confirmação do pedido
          </Typography>
          {loc.createdAt && (
            <Typography as="p" size="md" weight="regular" color="#3D3D3D">
              {`Pedido feito em ${loc.createdAt}`}
            </Typography>
          )}
        </div>
        <span style={isError ? errorBadgeStyle : mainBadgeStyle}>
          {isError ? <AlertIcon /> : <GearIcon />}
          {badgeLabel}
        </span>
      </div>

      {/* Bloco com borda (sem shadow) */}
      <Panel bordered shadow={false} padding="lg">
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: spacing.md,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: messageColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                as="span"
                size="sm"
                weight="bold"
                color={messageColor}
              >
                {loc.statusMessage}
              </Typography>
            </span>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <Typography as="span" size="sm" weight="regular" color="#3D3D3D">
                Nº do protocolo
              </Typography>
              <Typography as="span" size="sm" weight="medium" color="#3D3D3D">
                {loc.protocol}
              </Typography>
            </span>
          </div>

          <StatusStepper current={loc.currentStep ?? 0} isError={isError} />

          {typeof loc.description === 'string' ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.sm,
              }}
            >
              {htmlToParagraphs(loc.description).map((paragraph, i) => (
                <Typography
                  // parágrafos derivados de uma string estática → index é chave ok
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  as="p"
                  size="sm"
                  weight="regular"
                  color="#3D3D3D"
                >
                  {paragraph}
                </Typography>
              ))}
            </div>
          ) : (
            <Typography as="p" size="sm" weight="regular" color="#3D3D3D">
              {loc.description}
            </Typography>
          )}
        </div>
      </Panel>
    </div>
  );
};

const OrderConfirmation = ({
  locations,
  statusLabel = 'Em processamento',
  onBackHome,
}: OrderConfirmationProps): JSX.Element => {
  const total = locations.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <Typography as="h2" size="2xl" weight="bold" color="#0B29FB">
        Pedido finalizado
      </Typography>

      {locations.map((loc, i) => (
        <Panel key={loc.id} padding="lg">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.lg,
            }}
          >
            {/* Cabeçalho do local */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: spacing.md,
              }}
            >
              <Typography as="p" size="2xl" weight="bold" color="#0B29FB">
                {loc.title}
              </Typography>
              {total > 1 && (
                <Typography as="span" size="lg" weight="bold" color="#3D3D3D">
                  {`Central de abastecimento ${i + 1} de ${total}`}
                </Typography>
              )}
            </div>

            {/* Divisor tracejado (Figma: dashed [2,2] #D9D9D9) */}
            <div style={{ borderTop: '1px dashed #D9D9D9' }} />

            <ConfirmationBlock loc={loc} statusLabel={statusLabel} />
          </div>
        </Panel>
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" shape="pill" size="xl" onClick={onBackHome}>
          Voltar para a home
        </Button>
      </div>
    </div>
  );
};

export default OrderConfirmation;
